import {store} from "helpers/storage"

/** Cloudflare sends the campus subdomain as `?ss=` after redirecting to the main domain. */
export const BRANDING_SUBDOMAIN_QUERY_KEY = "ss"
export const BRANDING_SUBDOMAIN_STORAGE_KEY = "brandingSubdomain"
export const LOGIN_RETURN_QUERY_KEY = "to"

export class SlugHelper {
    public static generate(): string {
        const {host} = window.location
        const slug = process.env.NODE_ENV === "development" ? process.env.REACT_APP_SLUG_DEV : ""

        if (process.env.REACT_APP_SLUG_OVERRIDE) {
            return process.env.REACT_APP_SLUG_OVERRIDE
        }

        if (slug) return slug

        if (host.includes("10.10.10.10")) {
            return slug
        }
        let parts = host.split(".")
        if (parts.length >= 3) {
            return parts[0]
        } else {
            return slug
        }
    }

    /**
     * Campus branding subdomain. Cloudflare redirects
     * `chd.edular.com` → `douglasj.edular.com/login?tab=login&ss=chd`.
     */
    public static getSubdomain(): string | null {
        const override = process.env.REACT_APP_SUBDOMAIN_OVERRIDE
        if (override) {
            return SlugHelper.normalizeSubdomain(override)
        }

        const fromQuery = SlugHelper.readSubdomainFromQuery()
        if (fromQuery !== undefined) {
            SlugHelper.persistSubdomain(fromQuery)
            return fromQuery
        }

        if (SlugHelper.isLoginPath()) {
            SlugHelper.persistSubdomain(null)
            return null
        }

        return SlugHelper.normalizeSubdomain(store.get(BRANDING_SUBDOMAIN_STORAGE_KEY))
    }

    public static isGicClient(): boolean {
        return SlugHelper.generate() === "gic"
    }

    public static getLoginUrl(extra: Record<string, string> = {}): string {
        const params = new URLSearchParams({tab: "login", ...extra})
        const subdomain = SlugHelper.getSubdomain()
        if (subdomain) {
            params.set(BRANDING_SUBDOMAIN_QUERY_KEY, subdomain)
        }
        if (!params.has(LOGIN_RETURN_QUERY_KEY)) {
            const returnTo = SlugHelper.getReturnToPath()
            if (returnTo) {
                params.set(LOGIN_RETURN_QUERY_KEY, returnTo)
            }
        }

        return `/login?${params.toString()}`
    }

    private static getReturnToPath(): string | null {
        if (typeof window === "undefined" || SlugHelper.isLoginPath()) {
            return null
        }

        const returnTo = `${window.location.pathname}${window.location.search}`
        if (returnTo.length <= 1 || returnTo === "/") {
            return null
        }

        return returnTo
    }

    private static isLoginPath(): boolean {
        if (typeof window === "undefined") {
            return false
        }

        const path = window.location.pathname
        return path === "/login" || path.startsWith("/login/")
    }

    private static readSubdomainFromQuery(): string | null | undefined {
        if (typeof window === "undefined") {
            return undefined
        }

        const params = new URLSearchParams(window.location.search)
        if (!params.has(BRANDING_SUBDOMAIN_QUERY_KEY)) {
            return undefined
        }

        return SlugHelper.normalizeSubdomain(params.get(BRANDING_SUBDOMAIN_QUERY_KEY))
    }

    private static persistSubdomain(subdomain: string | null): void {
        if (subdomain) {
            store.set(BRANDING_SUBDOMAIN_STORAGE_KEY, subdomain)
            return
        }

        store.remove(BRANDING_SUBDOMAIN_STORAGE_KEY)
    }

    private static normalizeSubdomain(value: unknown): string | null {
        if (typeof value !== "string") {
            return null
        }

        const subdomain = value.trim().toLowerCase()
        return subdomain.length ? subdomain : null
    }
}
